import React, { useState, useRef, useEffect } from "react";
import { useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
/** CSS Files */
import "./ProjectStatusTest.scss";
/** Common Components */
import IconButton from "../../../../components/ui/iconbutton/IconButton";
import Iconify from "../../../../layouts/components/iconify/iconify";
import { DropdownWrapper } from "../../../../components/ui/dropdownwrapper/DropdownWrapper";
import Buttons from "../../../../components/ui/button/Buttons";
import CenterModel from "../../../../components/ui/centerModel/CenterModel";
import DataLoader from "../../../../components/ui/dataLoader/DataLoader";
/** RTK Query */
import {
  useAddProjectStatusMutation,
  useDeleteProjectStatusMutation,
  useGetProjectStatusWithPaginationMutation
} from "../../../../app/services/projectStatusAPI";
import { useLazyGetAllStatusCategoriesQuery } from "../../../../app/services/taskStatusAPI";
/** Common Services & Data Files */
import ToastService from "../../../../services/toastService/ToastService";
import SwalAlert from "../../../../services/swalService/SwalService";
import { decryptUrlData } from "../../../../services/CryptoService";
import AddProjectStatusTest from "./AddProjectStatusTest";

const CATEGORY_THEME_BY_ID = {
  1: "psc-not-started",
  2: "psc-active",
  3: "psc-review",
  4: "psc-testing",
  5: "psc-done",
  6: "psc-close",
};

const getStatusKey = (status) =>
  String(status.projectStatusConfigurationId ?? status.id);

const buildStatusCategories = (allCategories = [], projectStatuses = []) => {
  const statusesByCategory = projectStatuses.reduce((acc, status) => {
    const categoryId = status.statusCategoryId;
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(status);
    return acc;
  }, {});

  const categorySections = allCategories.map((category) => ({
    statusCategoryId: category.statusCategoryId,
    statusCategoryName: category.statusCategoryName,
    sortOrder: category.sortOrder ?? category.statusCategoryId,
    isCompleted: !!category.isCompleted,
    isTerminal: !!category.isTerminal,
    statuses: statusesByCategory[category.statusCategoryId] || [],
  }));

  const knownCategoryIds = new Set(
    categorySections.map((category) => category.statusCategoryId)
  );

  Object.keys(statusesByCategory).forEach((categoryIdKey) => {
    const categoryId = Number(categoryIdKey);
    if (knownCategoryIds.has(categoryId)) {
      return;
    }

    const firstStatus = statusesByCategory[categoryId][0];
    categorySections.push({
      statusCategoryId: categoryId,
      statusCategoryName: firstStatus?.statusCategoryName || "Unknown",
      sortOrder: categoryId,
      isCompleted: false,
      isTerminal: false,
      statuses: statusesByCategory[categoryId],
    });
  });

  return categorySections.sort(
    (a, b) =>
      (a.sortOrder || a.statusCategoryId || 0) -
      (b.sortOrder || b.statusCategoryId || 0)
  );
};

const ProjectStatusTest = () => {
  let projectId = null;
  const { id } = useParams();
  const projectdata = id ? decryptUrlData(id) : null;

  if (typeof projectdata === 'object' && projectdata !== null) {
    projectId = projectdata.projectId;
  } else if (projectdata) {
    projectId = projectdata;
  }
  /* State*/
  const [allStatusCategories, setAllStatusCategories] = useState([]);
  const [statusCategories, setStatusCategories] = useState([]);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [newName, setNewName] = useState("");
  const [isModelOpen, setIsModelOpen] = useState(false);
  const inputRef = useRef(null);
  const { confirm } = SwalAlert();
  // API'S Call
  const [addProjectStatus, {
    isLoading: isAdding,
    isSuccess: isProjectStatusAdded,
    data: addProjectStatusData
  },] = useAddProjectStatusMutation();

  const [deleteProjectStatus, {
    isLoading: isDeleting,
    isSuccess: isProjectStatusDeleted,
    data: deleteProjectStatusData
  },] = useDeleteProjectStatusMutation();

  const [getAllProjectStatusList, {
    isLoading: isProjectStatusListLoading,
    isSuccess: isProjectStatusListSuccess,
    data: isLProjectStatusListeData,
  },] = useGetProjectStatusWithPaginationMutation();

  const [getAllStatusCategoriesList, {
    isFetching: isStatusCategoriesLoading,
    isSuccess: isStatusCategoriesSuccess,
    data: statusCategoriesData,
  }] = useLazyGetAllStatusCategoriesQuery();

  //** useEffect's */

  useEffect(() => {
    getAllStatusCategoriesList();
    getProjectStatusList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isStatusCategoriesSuccess && statusCategoriesData) {
      setAllStatusCategories(statusCategoriesData);
    }
  }, [isStatusCategoriesSuccess, statusCategoriesData]);

  useEffect(() => {
    if (!isProjectStatusListSuccess || !isLProjectStatusListeData) {
      return;
    }

    const projectStatuses = isLProjectStatusListeData.dataSource || [];
    setStatusCategories(
      buildStatusCategories(allStatusCategories, projectStatuses)
    );
  }, [
    isProjectStatusListSuccess,
    isLProjectStatusListeData,
    allStatusCategories,
  ]);

  useEffect(() => {
    if (isProjectStatusAdded && addProjectStatusData) {
      if (addProjectStatusData.errorMessage.includes("exists")) {
        ToastService.warning(addProjectStatusData.errorMessage);
        return;
      }
      else {
        ToastService.success(addProjectStatusData.errorMessage);
        getProjectStatusList();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectStatusAdded, addProjectStatusData]);

  useEffect(() => {
    if (isProjectStatusDeleted && deleteProjectStatusData) {
      ToastService.success(deleteProjectStatusData.errorMessage);
      getProjectStatusList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProjectStatusDeleted, deleteProjectStatusData]);

  //** Funcation's */

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    if (
      source.droppableId === destination.droppableId &&
      source.index === destination.index
    ) {
      return;
    }

    const sourceCategoryIndex = parseInt(source.droppableId, 10);
    const destCategoryIndex = parseInt(destination.droppableId, 10);
    const newCategories = statusCategories.map((category) => ({
      ...category,
      statuses: [...category.statuses],
    }));

    const [movedStatus] = newCategories[sourceCategoryIndex].statuses.splice(
      source.index,
      1
    );

    const destCategory = newCategories[destCategoryIndex];
    const updatedStatus = {
      ...movedStatus,
      statusCategoryId: destCategory.statusCategoryId,
      isBacklog: destCategory.statusCategoryId === 1,
      isComplete:
        !!destCategory.isCompleted ||
        destCategory.statusCategoryId === 5 ||
        destCategory.statusCategoryId === 6,
    };

    destCategory.statuses.splice(destination.index, 0, updatedStatus);
    setStatusCategories(newCategories);
  };

  const getProjectStatusList = () => {
    const request = {
      pagination: {
        pageNumber: 1,
        pageSize: 1000,
      },
      projectId: projectId,
    };
    getAllProjectStatusList(request);
  };

  const onSidebarClose = () => {
    setIsModelOpen(false);
    setSelectedCategory(null);
  };

  const handleToggleModal = () => {
    setIsModelOpen(true);
  };

  const handleRenameClick = (status, currentName) => {
    setSelectedCategory(status)
    setIsModelOpen(true);
    setEditingStatusId(status.projectStatusConfigurationId);
    setNewName(currentName);
  };

  const handleBlur = (statusObj, value) => {
    if (!value) {
      getProjectStatusList();
      return;
    }
    const category = statusCategories.find(
      (item) => item.statusCategoryId === statusObj.statusCategoryId
    );
    const req = {
      isActive: statusObj.isActive,
      isBacklog: statusObj.statusCategoryId === 1,
      isComplete:
        !!category?.isCompleted ||
        statusObj.statusCategoryId === 5 ||
        statusObj.statusCategoryId === 6,
      isDefault: statusObj.isDefault,
      projectId: statusObj.projectId,
      statusCategoryId: statusObj.statusCategoryId,
      projectStatusConfigurationId: statusObj.projectStatusConfigurationId,
      statusName: value,
    }
    if (!statusObj.projectStatusConfigurationId) {
      addProjectStatus(req);
    }
  };

  const handleChange = (e) => {
    setNewName(e.target.value);
  };

  const handleInlineKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.target.blur();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setEditingStatusId(null);
      setNewName("");
      getProjectStatusList();
    }
  };

  const handleAddStatus = (category, categoryIndex) => {
    const newCategories = statusCategories.map((item) => ({
      ...item,
      statuses: [...item.statuses],
    }));
    const newStatus = {
      id: `${Date.now()}`,
      isActive: category.isActive || true,
      isBacklog: category.statusCategoryId === 1,
      isComplete:
        !!category.isCompleted ||
        category.statusCategoryId === 5 ||
        category.statusCategoryId === 6,
      isDefault: category.isDefault || false,
      projectId: projectId,
      statusCategoryId: category.statusCategoryId,
      statusName: '',
    };
    newCategories[categoryIndex].statuses.push(newStatus);
    setStatusCategories(newCategories);
    setEditingStatusId(newStatus.id);
    setNewName("");
  };

  const handleDeleteStatus = (status) => {
    let configurationId = status.projectStatusConfigurationId
    confirm(
      "Delete?",
      "Are you sure you want to Delete?",
      "Delete",
      "Cancel"
    ).then((confirmed) => {
      if (confirmed) {
        deleteProjectStatus(configurationId)
      }
    });
  };

  const getCategoryThemeClass = (category) =>
    CATEGORY_THEME_BY_ID[category.statusCategoryId] || "psc-not-started";

  const getPersistedStatusCount = (statuses = []) =>
    statuses.filter((status) => status.projectStatusConfigurationId).length;

  return (
    <>
      <div className="project-Status center-top-space m-2">
        <div className="d-flex justify-content-end mb-2">
          <Buttons
            fontIcon={true}
            textWithIcon={true}
            buttonTypeClassName="theme-btn"
            buttonText="Add Status"
            onClick={() => handleToggleModal()}
            iconName="material-symbols:add-rounded"
          />
        </div>
        {(isProjectStatusListLoading || isStatusCategoriesLoading || isDeleting || isAdding) ? <DataLoader /> : (
          <DragDropContext onDragEnd={onDragEnd}>
            <div className="project-status-container">
              {statusCategories.map((category, categoryIndex) => {
                const statusCount = getPersistedStatusCount(category.statuses);
                const hasVisibleStatuses = (category.statuses || []).length > 0;

                return (
                  <div
                    key={category.statusCategoryId || categoryIndex}
                    className={`project-status-section ${getCategoryThemeClass(category)}`}
                  >
                    <div className="project-status-category-section">
                      <div className="project-status-category-heading">
                        <div className="project-status-category">
                          {category.statusCategoryName || ""}
                        </div>
                        <span className="project-status-count" aria-label={`${statusCount} statuses`}>
                          {statusCount}
                        </span>
                      </div>
                      <div className="project-status-addbtn">
                        <IconButton
                          variant="text"
                          shape="square"
                          size="small"
                          icon="mingcute:add-fill"
                          onClick={() => handleAddStatus(category, categoryIndex)}
                          title="Add status in this category"
                        />
                      </div>
                    </div>
                    <Droppable droppableId={categoryIndex.toString()}>
                      {(provided, droppableSnapshot) => (
                        <div
                          className={`project-status-allstatus-section${droppableSnapshot.isDraggingOver ? " is-dragging-over" : ""}`}
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                        >
                          {!hasVisibleStatuses && (
                            <div className="project-status-empty-state">
                              No statuses — drop here or +
                            </div>
                          )}
                          {category?.statuses?.map((status, statusIndex) => {
                            const statusKey = getStatusKey(status);
                            const isEditing =
                              editingStatusId === status.id ||
                              editingStatusId === status.projectStatusConfigurationId;

                            return (
                              <Draggable
                                key={statusKey}
                                draggableId={statusKey}
                                index={statusIndex}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`project-status-allstatus-section-item${snapshot.isDragging ? " is-dragging" : ""}`}
                                    style={provided.draggableProps.style}
                                  >
                                    <div className="project-status-left-section">
                                      <span
                                        className="project-status-drag-handle"
                                        {...provided.dragHandleProps}
                                        title="Drag to reorder"
                                      >
                                        <Iconify
                                          icon="akar-icons:drag-vertical"
                                          width={14}
                                        />
                                      </span>
                                      <div className="project-status-allstatus-item">
                                        <span className="project-status-color-dot" aria-hidden="true" />
                                        <div className="project-status-allstatus-title">
                                          {isEditing ? (
                                            <input
                                              ref={inputRef}
                                              value={newName}
                                              onBlur={() => handleBlur(status, newName)}
                                              onChange={handleChange}
                                              onKeyDown={handleInlineKeyDown}
                                              autoFocus
                                              className="project-status-input"
                                              placeholder="Status name"
                                            />
                                          ) : (
                                            <>
                                              {status.statusName}
                                              {status.isDefault ? (
                                                <span className="project-status-default-badge">Default</span>
                                              ) : null}
                                            </>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="project-status-right-actionbtn">
                                      <DropdownWrapper
                                        toggleElement={
                                          <IconButton
                                            variant="text"
                                            shape="square"
                                            size="small"
                                            icon="proicons:more"
                                            style={{
                                              visibility: status.projectStatusConfigurationId
                                                ? "visible"
                                                : "hidden",
                                            }}
                                          />
                                        }
                                        direction="left"
                                        className="action-button-dropdown"
                                      >
                                        {!isModelOpen && (
                                          <>
                                            <button
                                              className="project-status-rename-btn"
                                              onClick={() => handleRenameClick(status, status.statusName)}
                                            >
                                              <Iconify icon="line-md:pencil" /> Edit
                                            </button>
                                            <button
                                              className="project-status-rename-btn"
                                              onClick={() => handleDeleteStatus(status)}
                                            >
                                              <Iconify icon="mingcute:delete-2-line" /> Delete
                                            </button>
                                          </>
                                        )}
                                      </DropdownWrapper>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </DragDropContext>
        )}
      </div>
      <CenterModel
        showModal={isModelOpen}
        handleToggleModal={onSidebarClose}
        modalTitle={selectedCategory ? "Update Status" : "Add Status"}
        modelSizeClass="w-40">
        <AddProjectStatusTest
          projectId={projectId}
          onSidebarClose={onSidebarClose}
          getProjectStatusList={getProjectStatusList}
          selectedCategory={selectedCategory}
        />
      </CenterModel>
    </>
  )
};

export default ProjectStatusTest;
